import { Card } from "@/components/ui/card";
import { Activity } from "lucide-react";

const stamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const CONNECTION_COLOR = {
  Healthy: "var(--c-emerald)",
  Unstable: "var(--c-amber)",
  "Needs attention": "var(--c-rose)",
};

const Row = ({ label, value, color, title, testid }) => (
  <div className="flex items-baseline justify-between gap-3 min-w-0" title={title}>
    <span className="text-[11px] text-[var(--tg-faint)] whitespace-nowrap">{label}</span>
    <span
      className="text-[12px] font-semibold tabular-nums truncate text-right"
      style={{ color: color || "var(--tg-text)" }}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

/**
 * Compact truthfulness panel. Every figure comes from /system/health, which reads
 * the local database and the in-process account registry only — it never polls
 * Telegram, so this can refresh on the dashboard's normal interval. Account
 * readiness here is the same account_ready() predicate the job engine gates on.
 */
export const SystemHealthCard = ({ health }) => {
  if (!health) return null;
  const a = health.accounts || {};
  const j = health.jobs || {};
  const connection = health.telegram_connection || "—";
  const integrityOk = health.account_reference_integrity === "OK";
  const tripped = Object.values(health.regression_guard || {}).filter((r) => r.tripped);

  return (
    <Card
      className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4"
      data-testid="system-health"
    >
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4" style={{ color: CONNECTION_COLOR[connection] || "var(--tg-faint)" }} />
        <div className="label-caps">System Health</div>
      </div>
      <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
        <Row
          label="Telegram accounts"
          value={`${a.ready ?? 0}/${a.total ?? 0} ready`}
          color={a.total && a.ready === a.total ? "var(--c-emerald)" : "var(--c-amber)"}
          title="Ready means an authorised client this process can actually use right now."
          testid="health-accounts"
        />
        <Row label="Copy jobs running" value={j.running ?? 0} testid="health-running" />
        <Row
          label="Waiting"
          value={j.waiting ?? 0}
          color={j.waiting ? "var(--c-amber)" : undefined}
          title="Running jobs currently blocked on an account that is reconnecting."
          testid="health-waiting"
        />
        <Row
          label="Configuration errors"
          value={j.configuration_errors ?? 0}
          color={j.configuration_errors ? "var(--c-rose)" : "var(--c-emerald)"}
          title="Jobs whose source or destination the assigned account cannot reach."
          testid="health-config-errors"
        />
        <Row
          label="Telegram connection"
          value={connection}
          color={CONNECTION_COLOR[connection]}
          testid="health-connection"
        />
        <Row
          label="Last startup recovery"
          value={stamp(health.last_startup_recovery)}
          testid="health-startup"
        />
        <Row
          label="Account reference integrity"
          value={health.account_reference_integrity || "—"}
          color={integrityOk ? "var(--c-emerald)" : "var(--c-rose)"}
          title="Every copy job still points at an account that exists."
          testid="health-integrity"
        />
        <Row
          label="Open incidents"
          value={health.open_incidents ?? 0}
          color={health.open_incidents ? "var(--c-amber)" : "var(--c-emerald)"}
          testid="health-incidents"
        />
      </div>
      {tripped.length > 0 && (
        <div
          className="mt-3 text-[11px] rounded-md px-3 py-2"
          style={{ color: "var(--c-rose)", background: "rgb(var(--c-rose-rgb)/0.08)" }}
          data-testid="health-regression"
        >
          Regression guard tripped: {tripped.map((r) => `${r.label} (${r.count}/${r.threshold})`).join(", ")}
        </div>
      )}
    </Card>
  );
};
